/**
 * <TutorVideoRecorder> — la caméra qui enregistre une réponse vidéo de la candidature (organism).
 *
 * L'ancien `VideoRecordingScreen` (route `video-recording`) et ses deux moteurs
 * (`VideoRecordingComponent` : `expo-camera` au téléphone, `react-webcam` + `MediaRecorder` au web).
 * Plein écran sur la nuit, la caméra frontale au fond ; en haut, sur un voile, la question et la
 * croix ; en bas, sur un voile, le temps qui reste et « Terminer ».
 *
 * Une question technique (`work`) garde le travail de l'élève ouvert pendant l'enregistrement : la
 * copie et la caméra, ensemble (voir `WorkRecorder`, plus bas). L'ancien écran le montrait en
 * vignettes qui s'ouvraient par-dessus la caméra, le temps et « Terminer ».
 *
 * Il n'y a ni déclencheur ni « Reprendre » : l'enregistrement part seul quelques secondes après
 * l'ouverture (`ready`, le temps qui ne court pas encore), s'arrête seul à la fin du temps, et le
 * candidat n'a qu'un essai. `<ActivityCopyCamera>` (une photo, son rond, « Reprendre » et
 * « Valider ») n'en a donc pas la forme ; il en garde le langage — la nuit, le voile, l'ouverture
 * dite en mots sans roue, l'accès refusé dit en phrase avec sa relance.
 *
 * Contrôlé, et sans rien de la caméra : le viseur (`viewfinder`) est posé par l'écran, la phase et le
 * temps arrivent en props, chaque geste est un rappel. Les deux confirmations (« Veux-tu vraiment
 * quitter ? », « … terminer l'enregistrement ? ») sont celles de la page.
 */
import React from "react"
import { View } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { StyleSheet } from "react-native-unistyles"

import {
    type UITutorRecorderPhase,
    type UITutorVideoQuestionWork,
} from "../../../ports/primary/ui/types/tutorCandidature"
import { formatCountdown } from "../../../ports/primary/ui/formatters/dateFormatter"
import { Badge } from "../../atoms/Badge"
import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { WaitingText } from "../../atoms/WaitingText"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import {
    TutorStudentWorkViewer,
    WORK_SIDE_WIDTH,
    WORK_SPLIT_AT,
    type TutorStudentWorkPart,
} from "../TutorStudentWorkViewer"

const TUTOR_VIDEO_RECORDER_TEXTS = {
    starting: "La caméra s'ouvre…",
    readySoon: "L'enregistrement démarre…",
    recording: "Enregistrement en cours",
    finish: "Terminer",
    close: "Quitter l'enregistrement",
    technicalHint: "Dans la vidéo tu peux faire comme si tu t'adressais à l'élève directement.",
    denied: "Pas d'accès à la caméra ou au microphone",
    allow: "Autoriser",
    back: "Retour",
}

/** Le viseur en vignette, au téléphone : de quoi se voir, la copie garde la place. */
const PIP_WIDTH = 108
const PIP_HEIGHT = 144
/**
 * Le liseré du viseur posé dans une case, vignette comme colonne. Il n'est pas qu'un dessin : sur
 * Android, une case arrondie qui coupe ce qui dépasse (`overflow: hidden`) SANS bordure cache la
 * caméra (sa surface native n'y perce plus la fenêtre), la case reste noire alors que l'enregistrement
 * tourne ; avec une bordure, l'image se voit (émulateur tablette, 03/11).
 */
const FRAME_BORDER = 2

const noop = () => {}

export type TutorVideoRecorderProps = {
    phase: UITutorRecorderPhase
    /** L'image de la caméra frontale, posée par l'écran. Elle remplit la page. */
    viewfinder?: React.ReactNode
    /** La question, avec « (copie d'un élève en 2nde) » pour une question technique. */
    question: string
    /** Le travail d'élève d'une question technique : ouvert à côté de la caméra, avec la phrase d'aide. */
    work?: UITutorVideoQuestionWork | null
    /** Le temps de réponse qui reste, en secondes. */
    secondsLeft: number
    onFinish: () => void
    onClose: () => void
    /** La page du travail d'élève ouverte à côté de la caméra. Défaut `copy`. */
    workPart?: TutorStudentWorkPart
    onChangeWorkPart?: (part: TutorStudentWorkPart) => void
    /** Accès refusé : redemander. */
    onAllow: () => void
    testID?: string
}

export function TutorVideoRecorder(props: TutorVideoRecorderProps) {
    const { phase, viewfinder, question, work, secondsLeft, onFinish, onClose, onAllow, testID } = props
    const insets = useSafeAreaInsets()
    const live = phase === "ready" || phase === "recording"

    // Une question technique garde la copie ouverte dès que la caméra s'ouvre ; un accès refusé repasse
    // par l'écran commun : sans caméra, il n'y a rien à enregistrer.
    if (work != null && phase !== "denied") return <WorkRecorder {...props} work={work} />

    return (
        <View style={styles.page} testID={testID}>
            <View style={styles.viewfinder}>{live ? viewfinder : null}</View>

            {phase === "starting" && (
                <View style={styles.centered}>
                    <WaitingText color="text-on-night">{TUTOR_VIDEO_RECORDER_TEXTS.starting}</WaitingText>
                </View>
            )}

            {phase === "denied" && (
                <View style={styles.centered}>
                    <Stack gap="4" align="center">
                        <Text size="text-xl" weight="semibold" color="text-on-night" center>
                            {TUTOR_VIDEO_RECORDER_TEXTS.denied}
                        </Text>
                        <Button label={TUTOR_VIDEO_RECORDER_TEXTS.allow} onPress={onAllow} />
                        <TextButton label={TUTOR_VIDEO_RECORDER_TEXTS.back} color="text-on-night" onPress={onClose} />
                    </Stack>
                </View>
            )}

            {live && (
                <>
                    <View style={styles.top(insets.top)}>
                        <Row gap="2" align="start">
                            <RecorderClose onClose={onClose} />
                            <Text size="text-md" weight="bold" color="text-on-night" center style={styles.question}>
                                {question}
                            </Text>
                            {/* La place de la croix, pour que la question reste au centre. */}
                            <View style={styles.balance} />
                        </Row>
                    </View>

                    <View style={styles.bottom(insets.bottom)}>
                        <Stack gap="3" align="center">
                            <RecorderStatus phase={phase} />
                            <RecorderTime secondsLeft={secondsLeft} size="text-3xl" />
                            {phase === "recording" && (
                                <View style={styles.finish}>
                                    <RecorderFinish onFinish={onFinish} />
                                </View>
                            )}
                        </Stack>
                    </View>
                </>
            )}
        </View>
    )
}

/**
 * L'enregistrement d'une question technique : la copie ET la caméra (Wajih, 29/10 : « il doit
 * s'enregistrer en étant en capacité de consulter en même temps une image »).
 *
 * Au téléphone, la copie remplit l'écran entre la question et « Terminer », et le viseur se tient en
 * vignette au coin bas, comme la visio d'un appel ; au grand écran, la copie à gauche, et à droite une
 * colonne : le viseur, le temps, la question, « Terminer ». Le temps et « Enregistrement en cours »
 * restent sous les yeux dans les deux cas.
 *
 * Un seul arbre dans les deux cas : au seuil, la copie et le viseur changent de place par le style
 * seulement. Le viseur est la caméra de l'écran (`expo-camera`, `MediaRecorder`) : remonté dans une
 * autre case, il couperait l'enregistrement. Au téléphone, la vignette est posée sur la barre du bas et
 * se lève au-dessus d'elle (`bottom: "100%"`).
 */
function WorkRecorder(props: TutorVideoRecorderProps & { work: UITutorVideoQuestionWork }) {
    const {
        phase,
        viewfinder,
        question,
        work,
        secondsLeft,
        onFinish,
        onClose,
        workPart = "copy",
        onChangeWorkPart,
        testID,
    } = props
    const insets = useSafeAreaInsets()
    const { onLayout, atLeast: split } = useWidthAtLeast(WORK_SPLIT_AT)

    return (
        <View style={styles.workPage(split, insets.top, insets.bottom)} testID={testID} onLayout={onLayout}>
            <View style={styles.workMain}>
                {!split && (
                    <View style={styles.phoneTop(insets.top)}>
                        <Row gap="1" align="start">
                            <RecorderClose onClose={onClose} />
                            <Stack gap="1" style={styles.phoneQuestion}>
                                <Text size="text-sm" weight="bold" color="text-on-night" numberOfLines={3}>
                                    {question}
                                </Text>
                                <Text size="text-xs" color="text-on-night">
                                    {TUTOR_VIDEO_RECORDER_TEXTS.technicalHint}
                                </Text>
                            </Stack>
                        </Row>
                        <Row justify="between" align="center" style={styles.phoneStatus}>
                            <RecorderStatus phase={phase} />
                            <RecorderTime secondsLeft={secondsLeft} size="text-xl" />
                        </Row>
                    </View>
                )}
                <View style={styles.workCopy(split)}>
                    <TutorStudentWorkViewer
                        work={work}
                        part={workPart}
                        onChangePart={onChangeWorkPart ?? noop}
                        tone="night"
                    />
                </View>
            </View>
            <View style={styles.workSide(split, insets.bottom)}>
                {split && (
                    <Row justify="between" align="center">
                        <RecorderStatus phase={phase} />
                        <RecorderClose onClose={onClose} />
                    </Row>
                )}
                <View style={split ? styles.sideCamera : styles.pip}>
                    {phase === "starting" ? (
                        // La caméra s'ouvre dans sa case : la copie, elle, est déjà là.
                        <View style={styles.cameraWaiting}>
                            <WaitingText color="text-on-night" size="text-sm" numberOfLines={2}>
                                {TUTOR_VIDEO_RECORDER_TEXTS.starting}
                            </WaitingText>
                        </View>
                    ) : (
                        viewfinder
                    )}
                </View>
                {split && <RecorderTime secondsLeft={secondsLeft} size="text-3xl" />}
                {split && (
                    <Stack gap="1">
                        <Text size="text-md" weight="bold" color="text-on-night">
                            {question}
                        </Text>
                        <Text size="text-sm" color="text-on-night">
                            {TUTOR_VIDEO_RECORDER_TEXTS.technicalHint}
                        </Text>
                    </Stack>
                )}
                {split && <View style={styles.sideSpacer} />}
                {phase === "recording" && <RecorderFinish onFinish={onFinish} />}
            </View>
        </View>
    )
}

/** Les contrôles de la caméra, les mêmes plein écran et à côté de la copie. */
function RecorderClose(props: { onClose: () => void }) {
    return (
        <IconButton
            iconName="times"
            appearance="transparent"
            size={44}
            iconColor="text-on-night"
            onPress={props.onClose}
            accessibilityLabel={TUTOR_VIDEO_RECORDER_TEXTS.close}
        />
    )
}

/** « Enregistrement en cours », ou « L'enregistrement démarre… » ; rien tant que la caméra s'ouvre. */
function RecorderStatus(props: { phase: UITutorRecorderPhase }) {
    if (props.phase === "recording") {
        return <Badge text={TUTOR_VIDEO_RECORDER_TEXTS.recording} variant="danger" />
    }
    if (props.phase !== "ready") return null
    return (
        <WaitingText color="text-on-night" size="text-sm">
            {TUTOR_VIDEO_RECORDER_TEXTS.readySoon}
        </WaitingText>
    )
}

function RecorderTime(props: { secondsLeft: number; size: "text-xl" | "text-3xl" }) {
    return (
        <Text size={props.size} weight="bold" color="text-on-night" accessibilityRole="timer">
            {formatCountdown(Math.max(0, props.secondsLeft))}
        </Text>
    )
}

function RecorderFinish(props: { onFinish: () => void }) {
    return <Button label={TUTOR_VIDEO_RECORDER_TEXTS.finish} color="danger" fullWidth onPress={props.onFinish} />
}

const styles = StyleSheet.create((theme) => ({
    page: { flex: 1, backgroundColor: theme.colors["surface-night"] },
    viewfinder: { position: "absolute", top: 0, left: 0, right: 0, bottom: 0 },
    centered: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: theme.spacing["6"],
    },
    top: (topInset: number) => ({
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        paddingTop: topInset + theme.spacing["2"],
        paddingBottom: theme.spacing["3"],
        paddingHorizontal: theme.spacing["2"],
        backgroundColor: theme.colors.scrim,
    }),
    question: { flex: 1, flexShrink: 1, minWidth: 0, paddingTop: theme.spacing["2"] },
    balance: { width: 44 },
    bottom: (bottomInset: number) => ({
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        paddingTop: theme.spacing["4"],
        paddingBottom: bottomInset + theme.spacing["5"],
        paddingHorizontal: theme.spacing["4"],
        backgroundColor: theme.colors.scrim,
    }),
    /** Une colonne de téléphone, même sur un grand écran, comme les choix de `<ActivityCopyCamera>`. */
    finish: { width: "100%", maxWidth: 480 },
    workPage: (split: boolean, top: number, bottom: number) => ({
        flex: 1,
        backgroundColor: theme.colors["surface-night"],
        flexDirection: split ? "row" : "column",
        paddingTop: split ? top + theme.spacing["5"] : 0,
        paddingBottom: split ? bottom + theme.spacing["5"] : 0,
        paddingHorizontal: split ? theme.spacing["5"] : 0,
        gap: split ? theme.spacing["5"] : 0,
    }),
    workMain: { flex: 1, minWidth: 0, minHeight: 0 },
    workCopy: (split: boolean) => ({
        flex: 1,
        minHeight: 0,
        paddingHorizontal: split ? 0 : theme.spacing["3"],
    }),
    workSide: (split: boolean, bottom: number) => ({
        width: split ? WORK_SIDE_WIDTH : undefined,
        gap: split ? theme.spacing["4"] : 0,
        paddingTop: split ? 0 : theme.spacing["3"],
        paddingBottom: split ? 0 : bottom + theme.spacing["4"],
        paddingHorizontal: split ? 0 : theme.spacing["4"],
    }),
    sideCamera: {
        width: "100%",
        aspectRatio: 4 / 3,
        borderRadius: theme.radius.lg,
        overflow: "hidden",
        borderWidth: FRAME_BORDER,
        borderColor: theme.colors.white,
        backgroundColor: theme.colors.black,
    },
    sideSpacer: { flex: 1 },
    cameraWaiting: { flex: 1, alignItems: "center", justifyContent: "center", padding: theme.spacing["2"] },
    phoneTop: (top: number) => ({
        paddingTop: top + theme.spacing["1"],
        paddingHorizontal: theme.spacing["2"],
        paddingBottom: theme.spacing["2"],
        gap: theme.spacing["1"],
    }),
    phoneQuestion: { flex: 1, minWidth: 0, paddingTop: theme.spacing["3"], paddingRight: theme.spacing["2"] },
    phoneStatus: { paddingHorizontal: theme.spacing["3"] },
    // Posée sur la barre du bas, elle se lève au-dessus d'elle, au coin de la copie.
    pip: {
        position: "absolute",
        right: theme.spacing["5"],
        bottom: "100%",
        marginBottom: theme.spacing["3"],
        width: PIP_WIDTH,
        height: PIP_HEIGHT,
        borderRadius: theme.radius.md,
        overflow: "hidden",
        borderWidth: FRAME_BORDER,
        borderColor: theme.colors.white,
        backgroundColor: theme.colors.black,
    },
}))
